import { SignUp, useClerk, useUser } from '@clerk/clerk-react';
import { ArrowLeft, Home, Loader2, LogOut } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useUserSyncContext } from '@/components/auth/UserSyncProvider/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_BASE_URL } from '@/config';
import { claimScopedAttempt, withAbortSignal } from '@/lib/authBootstrap/index';
import { buildLoginPath, signOutExplicitly } from '@/lib/authSession';
import {
  getAffiliateCheckoutStorage,
  readAffiliateCheckoutStorage,
} from '@/pages/public/affiliate-checkout';
import { getRegistrationRedirect, getRoleSelectionRedirect } from './registration-redirect';

const roleOptions = [
  {
    value: 'affiliate_owner',
    label: 'Gym Owner',
    description: 'Register your gym to access ErgShoot programming (£67.15/month)',
  },
  {
    value: 'individual',
    label: 'Individual',
    description: 'Access programming for personal training ($6.75/month)',
  },
] as const;

interface SavedAffiliateApplication {
  email?: string;
  gymName?: string;
  ownerName?: string;
}

function getSavedAffiliateApplication(): SavedAffiliateApplication {
  if (typeof window === 'undefined') return {};

  const savedData = readAffiliateCheckoutStorage(
    getAffiliateCheckoutStorage(),
    'affiliateApplication'
  );
  if (!savedData) return {};

  try {
    const parsed: unknown = JSON.parse(savedData);
    if (!parsed || typeof parsed !== 'object') return {};

    const record = parsed as Record<string, unknown>;
    return {
      email: typeof record.email === 'string' ? record.email : undefined,
      gymName: typeof record.gymName === 'string' ? record.gymName : undefined,
      ownerName: typeof record.ownerName === 'string' ? record.ownerName : undefined,
    };
  } catch {
    return {};
  }
}

function splitOwnerName(ownerName: string | undefined): {
  firstName?: string;
  lastName?: string;
} {
  const parts = ownerName?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return {};

  const [firstName, ...lastNameParts] = parts;
  return {
    firstName,
    ...(lastNameParts.length > 0 ? { lastName: lastNameParts.join(' ') } : {}),
  };
}

// FEAT-APR08-001: removed isPaidRole helper. Per the freemium model,
// affiliate_owner and individual no longer require payment at signup time —
// they land in the freemium shell with subscription_status='pending' and
// subscribe later from the in-product CTA.

export default function Register() {
  const navigate = useNavigate();
  const { resync } = useUserSyncContext();
  const [searchParams] = useSearchParams();
  const { isSignedIn, user, isLoaded } = useUser();
  const safeRedirect = getRegistrationRedirect(searchParams);
  const roleSelectionRedirect = getRoleSelectionRedirect(searchParams);
  const signInPath = buildLoginPath(roleSelectionRedirect, false);
  const isAffiliateApplicationFlow = safeRedirect === '/apply/affiliate';
  const savedAffiliateApplication = getSavedAffiliateApplication();
  const savedRole =
    typeof window !== 'undefined' ? localStorage.getItem('ergshoot_signup_role') : null;
  const urlRole = searchParams.get('role');
  const initialRole = urlRole || (isAffiliateApplicationFlow ? 'affiliate_owner' : savedRole || '');
  const signUpInitialValues = isAffiliateApplicationFlow
    ? {
        emailAddress: savedAffiliateApplication.email,
        ...splitOwnerName(savedAffiliateApplication.ownerName),
      }
    : undefined;

  // Wait for Clerk session to settle after redirect from signup
  // Clerk's session cookie may not be immediately available after redirect
  const [sessionSettled, setSessionSettled] = useState(false);

  useEffect(() => {
    // If already signed in, session is settled
    if (isLoaded && isSignedIn) {
      setSessionSettled(true);
      return;
    }

    // If Clerk is loaded but not signed in, wait a bit for session to settle
    // This handles the race condition after Clerk redirect
    if (isLoaded && !isSignedIn) {
      const timer = setTimeout(() => {
        console.log('[Register] Session settle timeout - proceeding with current state');
        setSessionSettled(true);
      }, 1500); // Wait 1.5 seconds for session to settle

      return () => clearTimeout(timer);
    }
  }, [isLoaded, isSignedIn]);

  const [selectedRole, setSelectedRole] = useState<string>(initialRole);
  const [username, setUsername] = useState('');
  const [affiliateName, setAffiliateName] = useState(
    isAffiliateApplicationFlow ? savedAffiliateApplication.gymName || '' : ''
  );
  const [isSettingRole, setIsSettingRole] = useState(false);
  const [isLinkingAccount, setIsLinkingAccount] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  // Capture agent referral code from URL
  const agentCode = searchParams.get('agent');

  // Check if user cancelled payment
  const paymentCancelled = searchParams.get('cancelled') === 'true';

  // Save role selection to localStorage when it changes
  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    localStorage.setItem('ergshoot_signup_role', role);
  };

  // Check if this is an invited user (has invited_user_id in publicMetadata)
  // NOTE: Clerk may or may not copy invitation metadata to user metadata
  const invitedUserId = user?.publicMetadata?.invited_user_id as string | undefined;
  const invitedRole = user?.publicMetadata?.role as string | undefined;
  const isInvitedUser = Boolean(invitedUserId);

  // Debug logging for invite flow diagnosis
  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      console.log('[Register] User loaded:', {
        clerkId: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        publicMetadata: user.publicMetadata,
        invitedUserId,
        invitedRole,
        isInvitedUser,
      });
    }
  }, [isLoaded, isSignedIn, user, invitedUserId, invitedRole, isInvitedUser]);

  // Check if user is signed in but needs role (and is NOT an invited user)
  const needsRoleSelection =
    isLoaded && isSignedIn && user && !user.publicMetadata?.role && !isInvitedUser;

  // Redirect based on role — delegates to DashboardRouter via '/' for consistency
  const redirectToRoleDashboard = useCallback(
    (_role: string) => {
      navigate(safeRedirect || '/');
    },
    [navigate, safeRedirect]
  );

  // Handle linking invited user accounts
  const linkInvitedAccount = useCallback(async () => {
    if (!invitedUserId || isLinkingAccount) return;

    // FEAT-APR08-001: SUPERSEDES BUG-007.
    // ALL invited users (including invited paid roles) now go through
    // link-invited-user and land in their dashboard. Members + coaches inherit
    // gym status (active if owner has paid, frozen otherwise — see D3 banner).
    // Invited owners/individuals land frozen and can subscribe in-product.
    setIsLinkingAccount(true);
    setLinkError(null);

    try {
      const token = await window.Clerk?.session?.getToken();
      if (!token) {
        setLinkError('Unable to authenticate. Please try again.');
        setIsLinkingAccount(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/link-invited-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ invited_user_id: invitedUserId }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.data.message || 'Account linked successfully!');
        // Force reload user to get updated metadata
        await user?.reload();
        resync();
        // Redirect to appropriate dashboard
        redirectToRoleDashboard(data.data.role);
      } else {
        setLinkError(data.error || 'Failed to link account');
      }
    } catch (error) {
      console.error('Error linking invited account:', error);
      setLinkError('An error occurred. Please try again.');
    } finally {
      setIsLinkingAccount(false);
    }
  }, [invitedUserId, isLinkingAccount, user, resync, redirectToRoleDashboard]);

  // Effect: If user is an invited user, link their account
  useEffect(() => {
    if (isLoaded && isSignedIn && isInvitedUser && !user?.publicMetadata?.role) {
      console.log('[Register] Detected invited user, calling link-invited-user...');
      linkInvitedAccount();
    }
  }, [isLoaded, isSignedIn, isInvitedUser, user?.publicMetadata?.role, linkInvitedAccount]);

  // State for auto-link in progress
  const [isAutoLinking, setIsAutoLinking] = useState(false);
  const [autoLinkError, setAutoLinkError] = useState<string | null>(null);
  const autoLinkAttemptedUserIdRef = useRef<string | null>(null);
  const autoLinkControllerRef = useRef<AbortController | null>(null);
  const redirectTimerRef = useRef<number | null>(null);
  const autoLinkIdentity = isSignedIn ? (user?.id ?? null) : null;

  // Cancel recovery only when this user leaves the registration flow. Clerk
  // metadata refreshes can change other effect dependencies while a successful
  // user.reload() is still settling, and must not cancel that same recovery.
  useEffect(() => {
    const identityAtSetup = autoLinkIdentity;
    return () => {
      // Do not let cleanup from an older render cancel a newer user's request.
      if (identityAtSetup !== null && autoLinkAttemptedUserIdRef.current !== identityAtSetup) {
        return;
      }
      autoLinkControllerRef.current?.abort();
      autoLinkControllerRef.current = null;
      if (redirectTimerRef.current !== null) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, [autoLinkIdentity]);

  // Fallback: If user is signed in but no invitation metadata, try to auto-link via /auth/me
  // The authMiddleware will auto-link if it finds a D1 user with matching email
  useEffect(() => {
    const tryAutoLink = async () => {
      if (!isLoaded || !isSignedIn || !user) return;
      if (user.publicMetadata?.role) return; // Already has role
      if (isInvitedUser) return; // Will be handled by linkInvitedAccount
      if (isLinkingAccount) return; // Already linking
      const attempt = claimScopedAttempt(autoLinkAttemptedUserIdRef.current, user.id);
      autoLinkAttemptedUserIdRef.current = attempt.attemptedScopeId;
      if (!attempt.shouldAttempt) return;

      console.log('[Register] No invitation metadata, trying auto-link via /auth/me...');
      setIsAutoLinking(true);
      setAutoLinkError(null);

      const controller = new AbortController();
      autoLinkControllerRef.current = controller;
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 10_000);

      try {
        const tokenPromise = window.Clerk?.session?.getToken();
        const token = tokenPromise ? await withAbortSignal(tokenPromise, controller.signal) : null;
        if (!token) {
          console.error('[Register] No token available for auto-link');
          setAutoLinkError('Unable to authenticate. Please try again.');
          setIsAutoLinking(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        const data = await response.json();
        console.log('[Register] /auth/me response:', data);

        if (data.success && data.data?.role) {
          // User was auto-linked by authMiddleware!
          toast.success(`Welcome! Your ${data.data.role.replace('_', ' ')} account is ready.`);

          // Try to reload Clerk user metadata, but don't fail if it errors
          try {
            await withAbortSignal(user.reload(), controller.signal);
          } catch (reloadError) {
            if (controller.signal.aborted) throw reloadError;
            console.warn('[Register] user.reload() failed, continuing anyway:', reloadError);
          }

          if (controller.signal.aborted) return;

          resync();

          // Small delay to ensure state is stable before redirect
          redirectTimerRef.current = window.setTimeout(() => {
            if (!controller.signal.aborted) {
              redirectToRoleDashboard(data.data.role);
            }
          }, 100);
        } else if (response.status === 404 || data.error === 'PROFILE_NOT_FOUND') {
          // User doesn't have a D1 profile - they need to select a role
          console.log('[Register] No D1 profile found, showing role selection');
          setIsAutoLinking(false);
        } else if (data.error) {
          console.error('[Register] Auto-link failed:', data.error);
          setAutoLinkError(data.error);
          setIsAutoLinking(false);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError' && !timedOut) return;
        console.error('[Register] Auto-link check failed:', error);
        setAutoLinkError(
          error instanceof Error && error.name === 'AbortError'
            ? 'Account setup timed out. Please try again.'
            : error instanceof Error
              ? error.message
              : 'Connection error'
        );
        setIsAutoLinking(false);
      } finally {
        clearTimeout(timeoutId);
        if (autoLinkControllerRef.current === controller) {
          autoLinkControllerRef.current = null;
        }
      }
    };

    // Small delay to let Clerk fully initialize
    const timer = setTimeout(tryAutoLink, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [
    isLoaded,
    isSignedIn,
    user,
    isInvitedUser,
    isLinkingAccount,
    resync,
    redirectToRoleDashboard,
  ]);

  const handleRoleSubmit = async () => {
    if (!user) return;

    setIsSettingRole(true);
    try {
      // FEAT-APR08-001: SUPERSEDES BUG-007.
      // ALL self-service roles (including affiliate_owner + individual) now go
      // through /auth/set-role and land directly in the freemium dashboard with
      // subscription_status='pending'. Stripe Checkout is triggered later from
      // the in-product Subscribe CTA, not as a hard wall on signup.
      const response = await fetch(`${API_BASE_URL}/auth/set-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await window.Clerk?.session?.getToken()}`,
        },
        body: JSON.stringify({
          role: selectedRole,
          username: username.trim().toLowerCase(),
          affiliate_name: selectedRole === 'affiliate_owner' ? affiliateName : undefined,
          agent_code: agentCode || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Differentiated copy: free coach vs frozen freemium owner/individual
        if (data.data?.is_frozen) {
          toast.success(
            'Welcome to Ergshoot — explore the platform. Subscribe any time to unlock premium features.'
          );
        } else {
          toast.success('Account created successfully!');
        }
        await user.reload();
        resync();
        redirectToRoleDashboard(selectedRole);
      } else {
        toast.error(data.error || 'Failed to create account');
      }
    } catch (error) {
      console.error('Error in role submit:', error);
      toast.error('Failed to complete signup');
    } finally {
      setIsSettingRole(false);
    }
  };

  const { signOut } = useClerk();

  const handleSignOut = async () => {
    // Clear signup state
    localStorage.removeItem('ergshoot_signup_role');
    await signOutExplicitly(signOut);
    navigate('/');
  };

  // Guard: don't render until Clerk is loaded AND session has settled
  // This prevents showing SignUp form briefly after Clerk redirect
  if (!isLoaded || !sessionSettled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Guard: redirect authenticated users who already have a role (no flash)
  if (isSignedIn && user?.publicMetadata?.role) {
    return <Navigate to={safeRedirect || '/'} replace />;
  }

  // Show loading state for auto-linking (invited users without metadata)
  if (isSignedIn && isAutoLinking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

        <Card className="w-full max-w-md animate-fade-in relative">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-gradient-gold">
                Welcome to ErgShoot!
              </CardTitle>
              <CardDescription className="mt-2">Setting up your account...</CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex flex-col items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground text-sm">Activating your account...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state for auto-link failure
  if (isSignedIn && autoLinkError && !isAutoLinking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

        <Card className="w-full max-w-md animate-fade-in relative">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-gradient-gold">
                Account Setup Issue
              </CardTitle>
              <CardDescription className="mt-2">
                There was a problem activating your account
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm">
              <p className="text-destructive font-medium">Error</p>
              <p className="text-muted-foreground">{autoLinkError}</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/')} className="flex-1">
                Go Home
              </Button>
              <Button
                onClick={() => {
                  setAutoLinkError(null);
                  window.location.reload();
                }}
                className="flex-1"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading/linking state for invited users
  if (isSignedIn && isInvitedUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

        <Card className="w-full max-w-md animate-fade-in relative">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-gradient-gold">
                Welcome to ErgShoot!
              </CardTitle>
              <CardDescription className="mt-2">
                {linkError
                  ? 'There was an issue activating your account'
                  : 'Setting up your account...'}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {linkError ? (
              <>
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm">
                  <p className="text-destructive font-medium">Error</p>
                  <p className="text-muted-foreground">{linkError}</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate('/')} className="flex-1">
                    Go Home
                  </Button>
                  <Button onClick={() => linkInvitedAccount()} className="flex-1">
                    Try Again
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground text-sm">Activating your account...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show role selection for signed-in users without a role
  if (needsRoleSelection) {
    return (
      <div className="min-h-screen bg-background">
        {/* Navigation Header */}
        <div className="border-b border-border">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link
              to="/"
              className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              <Home className="mr-2 h-5 w-5" />
              Home
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              <LogOut className="mr-2 h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-57px)]">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

          <Card className="w-full max-w-md animate-fade-in relative">
            <CardHeader className="text-center space-y-4">
              <div className="flex justify-center">
                <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
              </div>
              <div>
                <CardTitle className="text-3xl font-bold text-gradient-gold">
                  Welcome, {user.firstName}!
                </CardTitle>
                <CardDescription className="mt-2">
                  Select your account type to continue
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  autoComplete="username"
                  placeholder="Choose a public username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  maxLength={24}
                />
                <p className="text-xs text-muted-foreground">
                  3–24 characters: letters, numbers, underscores, or hyphens. This cannot be changed
                  later.
                </p>
              </div>

              {/* Payment Cancelled Notice */}
              {paymentCancelled && (
                <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3 text-sm">
                  <p className="text-yellow-500 font-medium">Payment Cancelled</p>
                  <p className="text-muted-foreground">
                    Your payment was cancelled. Select your account type below to try again.
                  </p>
                </div>
              )}

              {/* Role Selector */}
              <div className="space-y-2">
                <Label>I am a...</Label>
                <div className="grid gap-2">
                  {roleOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedRole === option.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <input
                        type="radio"
                        value={option.value}
                        checked={selectedRole === option.value}
                        onChange={(e) => handleRoleChange(e.target.value)}
                        className="mt-1 accent-primary"
                      />
                      <div>
                        <p className="font-medium text-foreground">{option.label}</p>
                        <p className="text-sm text-muted-foreground">{option.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Conditional: Gym Name for Affiliate Owners */}
              {selectedRole === 'affiliate_owner' && (
                <div className="space-y-2 animate-fade-in">
                  <Label htmlFor="affiliate_name">Gym Name</Label>
                  <Input
                    id="affiliate_name"
                    type="text"
                    placeholder="Your Gym Name"
                    value={affiliateName}
                    onChange={(e) => setAffiliateName(e.target.value)}
                  />
                </div>
              )}

              {/* Agent Referral Notice */}
              {agentCode && (
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-3 text-sm">
                  <p className="text-primary font-medium">Agent Referral</p>
                  <p className="text-muted-foreground">
                    You were referred by agent: <span className="text-foreground">{agentCode}</span>
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleRoleSubmit}
                className="w-full"
                size="lg"
                disabled={
                  isSettingRole ||
                  !selectedRole ||
                  !username.trim() ||
                  (selectedRole === 'affiliate_owner' && !affiliateName.trim())
                }
              >
                {isSettingRole ? 'Creating account...' : 'Create Account'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show Clerk SignUp for new users
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back
          </button>
          <Link
            to="/"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="mr-2 h-5 w-5" />
            Home
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-4 min-h-[calc(100vh-57px)]">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

        <div className="w-full max-w-md animate-fade-in relative">
          {/* Header with Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <h1 className="text-3xl font-bold text-gradient-gold">ErgShoot</h1>
            <p className="mt-2 text-muted-foreground">Create your account</p>
          </div>

          {/* Clerk SignUp Component */}
          <SignUp
            initialValues={signUpInitialValues}
            appearance={{
              elements: {
                rootBox: 'w-full',
                card: 'bg-card border border-border shadow-lg rounded-lg',
                headerTitle: 'hidden',
                headerSubtitle: 'hidden',
                socialButtonsBlockButton:
                  'bg-secondary hover:bg-secondary/80 text-foreground border-border',
                formFieldInput:
                  'bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary',
                formButtonPrimary: 'bg-primary hover:bg-primary/90 text-primary-foreground',
                footerActionLink: 'text-primary hover:text-primary/80',
                identityPreviewEditButton: 'text-primary hover:text-primary/80',
                formFieldLabel: 'text-foreground',
                dividerLine: 'bg-border',
                dividerText: 'text-muted-foreground',
                footer: 'hidden',
              },
              variables: {
                colorPrimary: '#FFBB38',
                colorBackground: '#1a1a1a',
                colorText: '#FAFAFA',
                colorInputBackground: '#141414',
                colorInputText: '#FAFAFA',
                borderRadius: '0.5rem',
              },
            }}
            signInUrl={signInPath}
            forceRedirectUrl={roleSelectionRedirect}
          />

          {/* Login Link */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <a
              href={signInPath}
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Sign in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
